<script setup lang="ts">
    import { toDateOnly } from '@shared/utils/formatDateTime';
    import { reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { loadInvoiceToPay, addPayment, PayableInvoice, PaymentResult } from './Billing.access';

    const state = reactive<{
        invoiceNumberInput: string;
        invoice: PayableInvoice | null;
        amount: number | null;
        payDate: string;
        acceptedDiff: number;
        paymentResult: PaymentResult | null;
        loading: boolean;
    }>({
        invoiceNumberInput: '',
        invoice: null,
        amount: null,
        payDate: toDateOnly(),
        acceptedDiff: 1,
        paymentResult: null,
        loading: false,
    });

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    async function getInvoice(): Promise<void> {
        if (!state.invoiceNumberInput) return;
        state.loading = true;
        state.invoice = null;
        state.paymentResult = null;
        try {
            state.invoice = await loadInvoiceToPay(state.invoiceNumberInput);
            state.amount = state.invoice.Amount + state.invoice.Vat;
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Could not load invoice' });
        } finally {
            state.loading = false;
        }
    }

    async function registerPayment(): Promise<void> {
        if (!state.invoice || state.amount == null) return;
        state.loading = true;
        try {
            state.paymentResult = await addPayment(state.invoice.InvoiceId, state.payDate, state.amount, state.acceptedDiff);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to register payment' });
        } finally {
            state.loading = false;
        }
    }
</script>

<template>
    <div class="d-flex flex-wrap align-items-end row">
        <div class="col-md-3">
            <div class="form-group">
                <label>Invoice number</label>
                <div class="input-group">
                    <input
                        type="text"
                        class="form-control"
                        placeholder="Press Enter to load"
                        v-model="state.invoiceNumberInput"
                        @keyup.enter="getInvoice" />
                    <span class="input-group-btn">
                        <button type="button" class="btn btn-default" :disabled="state.loading" @click="getInvoice">Get</button>
                    </span>
                </div>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Acceptable diff (sek)</label>
                <input type="number" class="form-control" v-model.number="state.acceptedDiff" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Amount</label>
                <input type="number" class="form-control" v-model.number="state.amount" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Pay date</label>
                <input type="date" class="form-control" v-model="state.payDate" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="state.loading || !state.invoice || state.invoice.Status === 'Paid' || state.paymentResult != null"
                    @click="registerPayment">
                    Register payment
                </button>
            </div>
        </div>
    </div>

    <div v-if="state.invoice">
        <h3 class="m-0-t m-lg-b">Payment details</h3>
        <div class="row">
            <div class="col-md-3">
                <span class="label-look d-block">Company name:</span>
                <p>{{ state.invoice.CompanyName }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Due date:</span>
                <p>{{ formatDate(state.invoice.DueDate) }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Invoice:</span>
                <p>{{ state.invoice.InvoiceNumber }}, amount: {{ state.invoice.Amount + state.invoice.Vat }}</p>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-3">
                <span class="label-look d-block">Amount:</span>
                <p>{{ state.invoice.Amount }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Vat:</span>
                <p>{{ state.invoice.Vat }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Total due:</span>
                <p>{{ state.invoice.Amount + state.invoice.Vat }}</p>
            </div>
        </div>
        <hr />
        <div class="row m-lg-b">
            <div class="col-md-3">
                <span class="label-look d-block">Amount already paid:</span>
                <p>{{ state.invoice.AmountPaid }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Status:</span>
                <p>{{ state.invoice.Status }}</p>
            </div>
            <div class="col-md-3">
                <span class="label-look d-block">Currency:</span>
                <p>{{ state.invoice.CurrencyCode }}</p>
            </div>
        </div>
    </div>

    <div v-if="state.paymentResult">
        <div
            class="alert"
            :class="{
                'alert-success': state.paymentResult.PaymentResult === 'PaidInFull' && !state.paymentResult.NeedsManualIntervention,
                'alert-warning': state.paymentResult.PaymentResult !== 'PaidInFull' || state.paymentResult.NeedsManualIntervention,
            }">
            <p>
                <i v-if="state.paymentResult.PaymentResult === 'PaidInFull' && !state.paymentResult.NeedsManualIntervention" class="fa fa-check"></i>
                <i v-else class="fa fa-exclamation-triangle"></i>
                <strong>Status:</strong> Payment accepted, {{ state.paymentResult.PaymentResult }}
            </p>
            <p v-if="state.paymentResult.AdditionalInformation">{{ state.paymentResult.AdditionalInformation }}</p>
            <p v-if="state.paymentResult.NeedsManualIntervention">Payment accepted, but subscription was not updated — needs to be managed manually</p>
        </div>
    </div>
</template>

<script lang="ts">
    export default { name: 'BillingRegisterPayment' };
</script>

<style></style>
